import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  GetSerieDetailResponse,
  GetSerieEconomicsResponse,
  TermEconomicsItem,
} from '@/api/generated/schemas';
import { LayerState, SerieLayer } from '@/api/generated/schemas';
import {
  getGetVenuaPlannerSerieDetailQueryKey,
  getGetVenuaPlannerSerieEconomicsQueryKey,
  useDeleteVenuaPlannerDraftTerms,
  useGetVenuaPlannerSerieDetail,
  useGetVenuaPlannerSerieEconomics,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { cn } from '@/shared/lib/utils';
import {
  Alert,
  Button,
  ConfirmDialog,
  FormActionBar,
  Panel,
  Tabs,
  WindowContent,
  toast,
  type TabItem,
} from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useWindowTitle, WindowHeaderExtras } from '@/shared/windowing';
import { venuaPlannerQueryKeys } from '../../venuaPlannerApi';
import { SerieBasicsForm } from './components/SerieBasicsForm';
import { serieDetailAgenda } from './serieDetailAgenda';
import { PublishSerieButton } from './publishSerie/PublishSerieButton';
import { termDetailAgenda } from '../termDetail/termDetailAgenda';
import { ApproveLayerButton } from '../internal/ApproveLayerButton';
import { useChangeSerieLayerState } from '../internal/useChangeLayerState';
import { SerieLayerPipeline } from '../internal/SerieLayerPipeline';
import { SerieLockedPane } from '../internal/SerieLockedPane';
import { CostSchemeTab } from '../internal/costScheme';
import { CalculationTab } from '../internal/calculation';
import { SerieStatsZone } from './components/SerieStatsZone';
import { SerieTermsTable } from './components/SerieTermsTable';
import { GenerateTermsDialog } from './generateTerms/GenerateTermsDialog';
import { formatDateRange } from './serieFormat';
import { SERIE_TAB_KEYS, toSerieStateKey, type SerieTabKey } from '../internal/serieLayers';
import { useSerieDetailForm } from './useSerieDetailForm';
import { TransportSchemeTab, useTransportSchemeEditor } from '../internal/transportScheme';

interface SerieDetailPageProps {
  layerId: string;
}

/**
 * Thin loader: vyřeší parametry agendy a načte detail + ekonomiku seriálu. Vlastní obrazovku (včetně
 * editačního formuláře nad TanStack Form) vykreslí až s načteným detailem, aby `useForm` dostal správné
 * výchozí hodnoty hned (žádný `form.reset`).
 */
export function SerieDetailPage({ layerId }: SerieDetailPageProps) {
  const { t } = useTranslation('venua-planner');
  const { open } = useAgendaParams(serieDetailAgenda, layerId);
  const { productId, serieId } = open;

  const detailQuery = useGetVenuaPlannerSerieDetail(productId, serieId);
  const economicsQuery = useGetVenuaPlannerSerieEconomics(productId, serieId);

  if (detailQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-phx-text-muted text-phx-sm">{t('serieDetail.loading')}</p>
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="p-phx-5 h-full">
        <Alert severity="error" title={t('serieDetail.loadErrorTitle')}>
          {t('serieDetail.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <SerieDetailView
      layerId={layerId}
      productId={productId}
      serieId={serieId}
      detail={detailQuery.data}
      economics={economicsQuery.data}
      isEconomicsLoading={economicsQuery.isLoading}
      // Intent z „+" u seriálu ve stromu: otevřít rovnou dialog „Přidat termíny".
      initialGenerateTermsOpen={open.addTerms ?? false}
    />
  );
}

interface SerieDetailViewProps {
  layerId: string;
  productId: string;
  serieId: string;
  detail: GetSerieDetailResponse;
  economics: GetSerieEconomicsResponse | undefined;
  isEconomicsLoading: boolean;
  initialGenerateTermsOpen: boolean;
}

/**
 * Detail termínového seriálu. Okamžitá data (metadata, stavy vrstev, termíny) přicházejí z `GetSerieDetail`,
 * ekonomika se dotahuje samostatně z `GetSerieEconomics` (do té doby skeletony). Údaje o seriálu se editují
 * přes TanStack Form a ukládají přes `UpdateSerie`.
 */
function SerieDetailView({
  layerId,
  productId,
  serieId,
  detail,
  economics,
  isEconomicsLoading,
  initialGenerateTermsOpen,
}: SerieDetailViewProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  // Záložka žije v URL stavu vrstvy (replace, ne push) — reload i deep-link zůstanou na stejné
  // vrstvě dat a výběr dalšího detailu ve stromu ji převezme (`inheritStateKeys`).
  const { state } = useAgendaParams(serieDetailAgenda, layerId);
  const agendaState = useAgendaState(serieDetailAgenda, layerId);
  const activeTab: SerieTabKey = state.tab ?? 'basics';
  const setActiveTab = useCallback((tab: SerieTabKey) => agendaState.patch({ tab }), [agendaState]);

  // Rozbalený termín na Yield Managementu — taky v URL, ať ho obnoví F5 i proklik z treeview.
  const expandedTermId = state.calcTermId ?? null;
  const setExpandedTermId = useCallback(
    (termId: string | null) =>
      termId ? agendaState.patch({ calcTermId: termId }) : agendaState.remove('calcTermId'),
    [agendaState]
  );

  const closeDetail = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const [generateTermsOpen, setGenerateTermsOpen] = useState(initialGenerateTermsOpen);
  const [deleteDraftsOpen, setDeleteDraftsOpen] = useState(false);

  // Detail termínu se otevírá na záložce ekvivalentní té, ze které se do něj kliklo — obě agendy
  // sdílejí klíče záložek (vrstev), takže stačí předat počáteční `state`.
  const openTerm = useCallback(
    (termGroupId: string) => {
      void navigator.openModal(
        termDetailAgenda,
        { productId, serieId, termId: termGroupId },
        { state: { tab: activeTab } }
      );
    },
    [navigator, productId, serieId, activeTab]
  );

  const { form, submit, isSubmitting } = useSerieDetailForm({
    productId,
    serieId,
    detail,
    onSaved: () => {
      void queryClient.invalidateQueries({
        queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
      });
      void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('serieDetail.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('serieDetail.toast.errorTitle'),
        description: t('serieDetail.toast.saveError'),
      });
    },
  });

  // Doprava má vlastní stavový hook volaný na úrovni stránky, aby spodní akční lišta fungovala
  // beze změny (activeSubmit) a nevznikal onSubmitReady callback.
  const transportEditor = useTransportSchemeEditor({
    productId,
    serieId,
    scope: 'serie',
    enabled: activeTab === 'operation',
  });

  const economicsByTerm = useMemo(() => {
    const map = new Map<string, TermEconomicsItem>();
    economics?.terms.forEach((term) => map.set(term.termGroupId, term));
    return map;
  }, [economics]);

  // Změna stavu vrstev seriálu (Schválit / Vrátit schválení) přes sjednocený endpoint – toasty i invalidace
  // řeší hook. Bez `serieLayers` u tlačítka (nad seriálem už žádná úroveň není) jde vždy jen o aktuální vrstvu.
  const { changeState, isPending: changing } = useChangeSerieLayerState({ productId, serieId });

  // Hromadné smazání termínů v přípravě (vrstva Termíny = Draft). Nabídne se jen když takový termín existuje.
  const draftTermsCount = detail.terms.filter((term) => term.termState === LayerState.Draft).length;
  const deleteDraftTerms = useDeleteVenuaPlannerDraftTerms();

  const handleDeleteDraftTerms = useCallback(async () => {
    setDeleteDraftsOpen(false);
    try {
      const response = await deleteDraftTerms.mutateAsync({ productId, serieId });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetVenuaPlannerSerieEconomicsQueryKey(productId, serieId),
        }),
        queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() }),
      ]);
      toast({
        severity: response.skippedCount > 0 ? 'warning' : 'success',
        description:
          response.skippedCount > 0
            ? t('serieDetail.terms.deleteDrafts.toast.deletedWithSkipped', {
                count: response.deletedCount,
                skipped: response.skippedCount,
              })
            : t('serieDetail.terms.deleteDrafts.toast.deleted', { count: response.deletedCount }),
      });
    } catch {
      toast({
        severity: 'error',
        title: t('serieDetail.toast.errorTitle'),
        description: t('serieDetail.terms.deleteDrafts.toast.error'),
      });
    }
  }, [deleteDraftTerms, productId, serieId, queryClient, t]);

  const rangeLabel = formatDateRange(detail.dateRangeFrom, detail.dateRangeTo);

  // Dynamický název okna (taskbar / historie / hlavička) = název seriálu + rozsah, např. „Podzim 2026 · NOV–DEC".
  useWindowTitle(`${detail.name} · ${rangeLabel}`);

  const operationLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('operation')
  );
  const operationStateKey = operationLayer ? toSerieStateKey(operationLayer.state) : 'locked';
  const operationLocked = operationStateKey === 'locked';
  // Doprava je read-only, jakmile je vrstva připravená / schválená; editovat lze jen v Draftu.
  const operationReadOnly = operationStateKey === 'prepared' || operationStateKey === 'approved';

  const termsLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('basics')
  );

  // Délka termínu je zamčená, jakmile seriál obsahuje termíny; typ dopravy po schválení vrstvy Termíny.
  const lengthLocked = detail.termsCount > 0;
  const transportLocked = termsLayer ? toSerieStateKey(termsLayer.state) !== 'draft' : false;

  // Doprava (Main Transport Scheme) je editovatelná jen ve stavu Draft; po přípravě/schválení je read-only.
  const operationEditable = activeTab === 'operation' && operationStateKey === 'draft';
  // Spodní akční lišta ukládá formulář aktivní záložky: na editovatelné operation (bus) Main Transport
  // Scheme, jinak základní údaje. Na neaktivních variantách operation tabu (locked / schváleno / non-bus)
  // se ukládání zakáže.
  const activeSubmit = operationEditable ? transportEditor.submit : submit;
  const activeSaving = operationEditable ? transportEditor.isSubmitting : isSubmitting;
  const saveDisabled = activeTab === 'operation' && !operationEditable;

  const tabItems: TabItem<SerieTabKey>[] = SERIE_TAB_KEYS.map((tabKey, index) => {
    const layer = detail.layers.find((entry) => entry.sortOrder === index);
    const locked = layer ? toSerieStateKey(layer.state) === 'locked' : false;
    return {
      value: tabKey,
      label: (
        <span className={cn('gap-phx-2 inline-flex items-center', locked && 'opacity-60')}>
          {t(`serieDetail.tabs.${tabKey}`)}
          {locked ? <Lock size={11} className="text-phx-text-faint" /> : null}
        </span>
      ),
    };
  });

  // Product cost scheme (Cost Scheme agenda) — full-bleed board that takes over the whole content area
  // and renders its own footer, so it must not sit inside the centered scrolling column + FormActionBar.
  const productCostLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('productCost')
  );
  const productCostLocked = productCostLayer
    ? toSerieStateKey(productCostLayer.state) === 'locked'
    : true;
  const showCostBoard = activeTab === 'productCost' && !productCostLocked;

  // Full-bleed stats pruh (bez odsazení) — v normální větvi jde do `banner` slotu WindowContent,
  // v cost-board větvi se vykreslí nad fixní hlavičku.
  const statsBand = (
    <SerieStatsZone detail={detail} economics={economics} isEconomicsLoading={isEconomicsLoading} />
  );
  const pipelineEl = (
    <>
      {/* Podtitulek okna (portál do hlavičky) = produkt seriálu, např. „Andaluzja Express". */}
      <WindowHeaderExtras subtitle={detail.productName} />
      <SerieLayerPipeline
        layers={detail.layers}
        actions={<PublishSerieButton productId={productId} serieId={serieId} />}
      />
    </>
  );
  const tabsEl = (
    <Tabs<SerieTabKey>
      variant="underline"
      value={activeTab}
      onChange={setActiveTab}
      items={tabItems}
    />
  );

  // Akce nad seznamem termínů — stejná dvojice v hlavičce i patičce panelu. Hromadné smazání se nabízí
  // jen tehdy, když seriál nějaký termín v přípravě opravdu má.
  const termsActions = (
    <>
      {draftTermsCount > 0 ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconLeft="trash"
          className="text-phx-danger hover:bg-phx-danger-tint hover:border-phx-danger"
          disabled={deleteDraftTerms.isPending}
          onClick={() => setDeleteDraftsOpen(true)}
        >
          {t('serieDetail.terms.deleteDrafts.action')}
        </Button>
      ) : null}
      <Button type="button" size="sm" iconLeft="plus" onClick={() => setGenerateTermsOpen(true)}>
        {t('serieDetail.terms.addTerms')}
      </Button>
    </>
  );

  if (showCostBoard) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="serie-detail">
        {statsBand}
        <div className="px-phx-5 pt-phx-5 shrink-0">
          <div className="gap-phx-3 flex w-full flex-col">
            {pipelineEl}
            {tabsEl}
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <CostSchemeTab
            scope="serie"
            productId={productId}
            serieId={serieId}
            layers={detail.layers}
            onBack={closeDetail}
          />
        </div>
      </div>
    );
  }

  // Calculation — full-bleed board s vlastní patičkou (Uložit / Uložit a schválit), stejně jako Cost Scheme.
  const calculationLayer = detail.layers.find(
    (entry) => entry.sortOrder === SERIE_TAB_KEYS.indexOf('calculation')
  );
  const calculationLocked = calculationLayer
    ? toSerieStateKey(calculationLayer.state) === 'locked'
    : true;
  const showCalculationBoard = activeTab === 'calculation' && !calculationLocked;

  if (showCalculationBoard) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="serie-detail">
        {statsBand}
        <div className="px-phx-5 pt-phx-5 shrink-0">
          <div className="gap-phx-3 flex w-full flex-col">
            {pipelineEl}
            {tabsEl}
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <CalculationTab
            scope="serie"
            productId={productId}
            serieId={serieId}
            layers={detail.layers}
            onBack={closeDetail}
            expandedTermId={expandedTermId}
            onExpandedTermIdChange={setExpandedTermId}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <WindowContent
        as="div"
        data-testid="serie-detail"
        banner={statsBand}
        footer={
          <FormActionBar
            onBack={closeDetail}
            onSave={activeSubmit}
            saving={activeSaving}
            disabled={saveDisabled}
            extraActions={
              activeTab === 'basics' ? (
                // Bez `serieLayers` → žádná nabídka „až po X“ (nad seriálem už žádná úroveň není).
                <ApproveLayerButton
                  currentLayer={SerieLayer.Terms}
                  layers={detail.layers}
                  onBeforeAction={submit}
                  onChangeState={changeState}
                  disabled={isSubmitting}
                  loading={changing}
                />
              ) : activeTab === 'operation' && !operationLocked ? (
                <ApproveLayerButton
                  currentLayer={SerieLayer.OperationScheme}
                  layers={detail.layers}
                  onBeforeAction={operationEditable ? transportEditor.submit : undefined}
                  onChangeState={changeState}
                  disabled={transportEditor.isSubmitting}
                  loading={changing}
                />
              ) : undefined
            }
          />
        }
      >
        {pipelineEl}

        {tabsEl}

        {activeTab === 'basics' ? (
          <form
            className="gap-phx-3 flex flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit();
            }}
          >
            <Panel variant="filled" title={t('serieDetail.basics.panelTitle')}>
              <SerieBasicsForm
                form={form}
                dateRange={rangeLabel}
                lengthLocked={lengthLocked}
                transportLocked={transportLocked}
              />
            </Panel>

            <Panel
              variant="filled"
              icon="calendar"
              title={t('serieDetail.terms.panelTitle')}
              subtitle={t('serieDetail.terms.subtitle', {
                count: detail.termsCount,
                range: rangeLabel,
              })}
              padded={false}
              actions={
                <div className="gap-phx-3 flex items-center">
                  {isEconomicsLoading ? (
                    <span className="text-phx-text-muted gap-phx-2 text-phx-sm inline-flex items-center whitespace-nowrap">
                      <span className="bg-phx-primary h-phx-3_5 w-phx-3_5 animate-pulse rounded-full" />
                      {t('serieDetail.terms.loadNote')}
                    </span>
                  ) : null}
                  {termsActions}
                </div>
              }
              footer={
                <div className="flex w-full items-center">
                  <span className="text-phx-text-muted">
                    {t('serieDetail.terms.footerCount', {
                      shown: detail.termsCount,
                      total: detail.termsCount,
                    })}
                  </span>
                  <span className="gap-phx-3 ml-auto flex items-center">{termsActions}</span>
                </div>
              }
            >
              <SerieTermsTable
                terms={detail.terms}
                economicsByTerm={economicsByTerm}
                currency={economics?.currency ?? ''}
                isEconomicsLoading={isEconomicsLoading}
                onOpenTerm={openTerm}
              />
            </Panel>
          </form>
        ) : activeTab === 'operation' ? (
          operationLocked ? (
            <SerieLockedPane title={t('serieDetail.tabs.operation')} />
          ) : (
            <TransportSchemeTab
              editor={transportEditor}
              productId={productId}
              serieId={serieId}
              scope="serie"
              readOnly={operationReadOnly}
              onOpenTerm={openTerm}
            />
          )
        ) : (
          <SerieLockedPane title={t(`serieDetail.tabs.${activeTab}`)} />
        )}
      </WindowContent>

      {generateTermsOpen ? (
        <GenerateTermsDialog
          productId={productId}
          serieId={serieId}
          onClose={() => setGenerateTermsOpen(false)}
          allowLengthOverride={detail.termsCount === 0}
        />
      ) : null}

      {deleteDraftsOpen ? (
        <ConfirmDialog
          title={t('serieDetail.terms.deleteDrafts.confirmTitle')}
          message={t('serieDetail.terms.deleteDrafts.confirmMessage', { count: draftTermsCount })}
          confirmLabel={t('serieDetail.terms.deleteDrafts.confirmAction')}
          cancelLabel={t('serieDetail.terms.deleteDrafts.cancel')}
          onConfirm={() => void handleDeleteDraftTerms()}
          onCancel={() => setDeleteDraftsOpen(false)}
        />
      ) : null}
    </>
  );
}
